export type GraphicsAccelerationStatus = 'software' | 'unknown';

export const GRAPHICS_WARNING_SESSION_KEY = 'nordrevived.graphics-warning.v1';
export const GRAPHICS_WARNING_TITLE = 'Improve game performance';

// Match explicit CPU renderer names only. ANGLE, Mesa and a generic Microsoft vendor
// can all describe accelerated graphics, and absent information proves nothing.
const SOFTWARE_RENDERER =
  /\b(?:swiftshader|llvmpipe|softpipe|software rasterizer|microsoft basic render driver)\b/i;

export function detectGraphicsAcceleration(context: {
  RENDERER: number;
  getExtension(
    name: 'WEBGL_debug_renderer_info',
  ): Pick<WEBGL_debug_renderer_info, 'UNMASKED_RENDERER_WEBGL'> | null;
  getParameter(parameter: number): unknown;
}): GraphicsAccelerationStatus {
  try {
    const debug = context.getExtension('WEBGL_debug_renderer_info');
    const renderer: unknown = context.getParameter(
      debug?.UNMASKED_RENDERER_WEBGL ?? context.RENDERER,
    );
    return typeof renderer === 'string' && SOFTWARE_RENDERER.test(renderer)
      ? 'software'
      : 'unknown';
  } catch {
    return 'unknown';
  }
}

export function graphicsAccelerationInstructions(userAgent: string): string {
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(userAgent)) {
    return 'Update your browser and device software, then restart your browser. If the problem continues, try another supported browser or device.';
  }
  let steps: string;
  if (userAgent.includes('Edg/')) {
    steps =
      'In Microsoft Edge, paste edge://settings/system into the address bar. Turn on “Use graphics acceleration when available”, then restart Edge.';
  } else if (userAgent.includes('Firefox/')) {
    steps =
      'In Firefox Settings, find Performance. Uncheck “Use recommended performance settings”, turn on hardware acceleration, then restart Firefox.';
  } else if (userAgent.includes('Chrome/') && !userAgent.includes('OPR/')) {
    steps =
      'In your browser Settings, open System. Turn on “Use graphics acceleration when available”, then restart your browser.';
  } else {
    return 'Update your browser and device software, then restart your browser. If the problem continues, try another supported browser or device.';
  }
  return `${steps}\n\nIf the setting is unavailable or already on, try updating your browser and graphics driver.`;
}

export function graphicsAccelerationWarning(userAgent: string): string {
  return `Hardware graphics acceleration appears unavailable. Nord can run much more smoothly when your browser uses your graphics processor.\n\n${graphicsAccelerationInstructions(userAgent)}`;
}

export class GraphicsInitializationError extends Error {
  constructor(cause: unknown) {
    super('Nord could not initialize 3D graphics.', { cause });
    this.name = 'GraphicsInitializationError';
  }
}

export function worldInitializationErrorMessage(error: unknown, userAgent: string): string {
  if (error instanceof GraphicsInitializationError) {
    return `${error.message} Hardware graphics acceleration may be unavailable.\n\n${graphicsAccelerationInstructions(userAgent)}`;
  }
  return error instanceof Error && error.message.length > 0
    ? error.message
    : 'The village could not be displayed.';
}

export class GraphicsAccelerationWarningSession {
  private shown = false;

  constructor(
    private readonly storage: () => Pick<Storage, 'getItem' | 'setItem'> = () => sessionStorage,
  ) {}

  shouldShow(status: GraphicsAccelerationStatus): boolean {
    if (status !== 'software' || this.shown) return false;
    try {
      return this.storage().getItem(GRAPHICS_WARNING_SESSION_KEY) !== '1';
    } catch {
      return true;
    }
  }

  markShown(): void {
    this.shown = true;
    try {
      this.storage().setItem(GRAPHICS_WARNING_SESSION_KEY, '1');
    } catch {
      // Memory still prevents repeat warnings during travel if browser storage is blocked.
    }
  }
}
